import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FeeComponentProps, ParsedFeeDisclaimer } from '../../types';
import {
  checkAreDifferentFees,
  EPaymentMethod,
  filterFeesByCardType,
  filterFeesByPaymentType,
  getMaxFee,
  parseFeesDisclaimers,
} from '../../utils';
import { useDataContext } from '../../context/dataContainer';

export const ClientFee = ({ paymentType, potentialCardType, currentLanguage = 'en' }: FeeComponentProps) => {
  const { t } = useTranslation('fees');
  const { clientFeesDisclaimers } = useDataContext();

  const feeNF = new Intl.NumberFormat(currentLanguage, {
    minimumFractionDigits: 2,
  });

  const { suitableFeeDisclaimer, areDifferentFees } = useMemo<{
    suitableFeeDisclaimer: ParsedFeeDisclaimer | null | undefined;
    areDifferentFees: boolean;
  }>(() => {
    const parsedFeesDisclaimers = parseFeesDisclaimers(clientFeesDisclaimers);
    const filteredFeesByPaymentType = filterFeesByPaymentType(parsedFeesDisclaimers, paymentType);

    if (paymentType && paymentType === EPaymentMethod.ACH_ELECTRONIC_CHECK) {
      return {
        suitableFeeDisclaimer: filteredFeesByPaymentType[0],
        areDifferentFees: false,
      };
    }

    if (paymentType && paymentType === EPaymentMethod.PAY_WITH_CREDIT_CARD && potentialCardType) {
      return {
        suitableFeeDisclaimer: filterFeesByCardType(filteredFeesByPaymentType, potentialCardType)[0],
        areDifferentFees: checkAreDifferentFees(filteredFeesByPaymentType),
      };
    }

    return {
      suitableFeeDisclaimer: getMaxFee(filteredFeesByPaymentType),
      areDifferentFees: checkAreDifferentFees(filteredFeesByPaymentType),
    };
  }, [paymentType, potentialCardType, clientFeesDisclaimers]);

  const feeText = useMemo(() => ((
    suitableFeeDisclaimer &&
    typeof suitableFeeDisclaimer.disclaimer === 'object' &&
    suitableFeeDisclaimer.disclaimer.data
  )
    ? t(suitableFeeDisclaimer.disclaimer.key, {
      fee: feeNF.format(Number(suitableFeeDisclaimer.disclaimer.data[0])),
      upTo: areDifferentFees && !potentialCardType ? 'up to ' : '',
      a: !areDifferentFees || potentialCardType ? 'a ' : '',
      cardType: areDifferentFees && potentialCardType ? `${potentialCardType} ` : '',
      withCardType: areDifferentFees && potentialCardType ? ` with ${potentialCardType}` : '',
    })
    : (suitableFeeDisclaimer?.disclaimer as string) || ''
  ), [suitableFeeDisclaimer, areDifferentFees]);

  if (!suitableFeeDisclaimer) {
    return null;
  }

  return (
    <div className='fee-info'>
      <p>{feeText}</p>
    </div>
  );
};
